import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
  formatContractDate,
  withFallback,
} from "kontrolEdge/helpers/formattersShappedData";
import {
  resolveServiceStatusTag,
  serviceStatusRank,
} from "kontrolEdge/helpers/serviceStatusTag";
import { CARRIER_BRIDGE_LABEL } from "kontrolEdge/helpers/carrierBridgeName";

/**
 * One Carrier Bridge as the customer endpoint returns it
 * (`GET /v1/kontroledge/carrier-bridge`, chopper-backend CTX-718 / CTX-719).
 */
export interface CarrierBridgeResponse {
  id: number;
  subscriptionId: string | null;
  name: string | null;
  carrier: { id: number; name: string; code: string | null } | null;
  port: {
    id: number;
    subid: string | null;
    name: string | null;
    capacityMbps: number | null;
    datacenter: { datacenterId: string; name: string | null; city: string | null } | null;
  } | null;
  /** Null means the whole port goes to the carrier. */
  vlan: string | null;
  wholePort: boolean;
  mbps: number;
  circuitId: string | null;
  /** Live status from the port's state and traffic (CTX-719). */
  serviceStatus: string;
  serviceStatusMessage: string | null;
  subscription: {
    renewDate?: string | null;
    term?: number | null;
    term_unit?: string | null;
    contractEnd?: string | null;
    state?: string | null;
  } | null;
  tenantId: number;
  customer: { name: string; domain?: string | null; chargebeeCustomerId?: string | null } | null;
  /** Pivot-backed tags; arrive once the Tags Manager knows the type (CTX-721). */
  tags?: unknown;
}

export interface CarrierBridge {
  id: number;
  serviceId: string;
  name: string;
  serviceStatus: string;
  serviceStatusMessage: string | null;
  tagsRich?: ResourceTagSummary[];
  carrierName: string;
  portSubid: string;
  siteName: string;
  vlanLabel: string;
  bandwidth: string;
  mbps: number;
  term: string;
  renewDate: string;
  renewDateRaw: Date | null;
  tenantId: number;
  customer?: { name: string; chargebeeCustomerId: string };
}

export const carrierBridgeIdHeader = `${CARRIER_BRIDGE_LABEL} ID`;

export const carrierBridgeColumns: GenericTableColumn<CarrierBridge>[] = [
  { header: carrierBridgeIdHeader, accessor: "serviceId" },
  { header: "Name", accessor: "name" },
  {
    header: "Status",
    renderCell: (row) => {
      const { label, variant } = resolveServiceStatusTag(row.serviceStatus);
      return <GenericTag label={label} variant={variant} size="small" />;
    },
    sortAccessor: (row) => serviceStatusRank(row.serviceStatus),
  },
  {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
  },
  { header: "Carrier", accessor: "carrierName" },
  {
    header: "Port",
    sortAccessor: (row) => row.portSubid,
    renderCell: (row) => (
      <div className="flex flex-col leading-tight">
        <span>{row.portSubid}</span>
        {row.siteName !== "-" && (
          <span className="text-xs text-semantic-text-secondary">{row.siteName}</span>
        )}
      </div>
    ),
  },
  { header: "VLAN", accessor: "vlanLabel" },
  {
    header: "Bandwidth",
    accessor: "bandwidth",
    sortAccessor: (row) => row.mbps,
  },
  { header: "Term", accessor: "term" },
  {
    header: "Renew Date",
    accessor: "renewDate",
    sortAccessor: (row) => row.renewDateRaw ?? "",
  },
];

export function shapeCarrierBridgeData(
  rows: CarrierBridgeResponse[]
): CarrierBridge[] {
  return rows.map((r) => {
    const subscription = r.subscription ?? null;
    const renewIso =
      subscription?.renewDate && subscription.renewDate !== "N/A"
        ? subscription.renewDate
        : null;
    const rawDate = renewIso ? new Date(renewIso) : null;
    const datacenter = r.port?.datacenter;

    return {
      id: r.id,
      serviceId: withFallback(r.subscriptionId),
      name: withFallback(r.name),
      serviceStatus: r.serviceStatus ?? "unknown",
      serviceStatusMessage: r.serviceStatusMessage ?? null,
      tagsRich: mapKeListResourceTags(r),
      carrierName: withFallback(r.carrier?.name),
      portSubid: withFallback(r.port?.subid ?? r.port?.name),
      siteName: datacenter
        ? [datacenter.name ?? datacenter.datacenterId, datacenter.city]
            .filter(Boolean)
            .join(" · ")
        : "-",
      // A null VLAN is the whole port; the approved design shows it as "-".
      vlanLabel: r.vlan ?? "-",
      bandwidth: `${r.mbps} Mbps`,
      mbps: r.mbps,
      // Term and renewal are the subscription's (CTX-805); none yet reads "-".
      term: subscription?.term
        ? `${subscription.term} ${withFallback(subscription.term_unit)}`
        : "-",
      renewDate: renewIso ? formatContractDate(renewIso) : "-",
      renewDateRaw: rawDate && !isNaN(rawDate.getTime()) ? rawDate : null,
      tenantId: r.tenantId,
      customer: r.customer
        ? {
            name: r.customer.name ?? "-",
            chargebeeCustomerId: r.customer.chargebeeCustomerId ?? "-",
          }
        : undefined,
    };
  });
}
